import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Cpu } from 'lucide-react';
import { getLlmModelBadgeByKey, getLlmModelByKey, LLM_MODELS } from '../../constants/llmModels';
import { useLlmCatalog } from '../../hooks/useLlmCatalog';
import { classNames } from '../../utils/classNames';

const MENU_GAP = 8;
const MENU_MAX_HEIGHT = 256;

const PROVIDER_DOT_CLASS = {
  GapGPT: 'bg-blue-500 ring-blue-500/35',
  AvalAI: 'bg-emerald-500 ring-emerald-500/35'
};

const ProviderDot = ({ provider, size = 'sm' }) => (
  <span
    className={classNames(
      'inline-block shrink-0 rounded-full ring-2',
      size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5',
      PROVIDER_DOT_CLASS[provider] || 'bg-slate-400 ring-slate-400/35'
    )}
    title={provider}
    aria-hidden="true"
  />
);

const BADGE_STYLE_CLASS = {
  اقتصادی:
    'border-slate-300/80 bg-slate-100 text-slate-600 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-300',
  متوسط:
    'border-amber-300/80 bg-amber-50 text-amber-700 dark:border-amber-600/50 dark:bg-amber-950/50 dark:text-amber-300',
  خوب:
    'border-sky-300/80 bg-sky-50 text-sky-700 dark:border-sky-600/50 dark:bg-sky-950/50 dark:text-sky-300',
  عالی:
    'border-violet-300/80 bg-violet-50 text-violet-700 dark:border-violet-600/50 dark:bg-violet-950/50 dark:text-violet-300',
  'حرفه ای':
    'border-violet-300/80 bg-violet-50 text-violet-700 dark:border-violet-600/50 dark:bg-violet-950/50 dark:text-violet-300'
};

const ModelBadge = ({ label }) => {
  if (!label) {
    return null;
  }

  return (
    <span
      className={classNames(
        'inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold leading-none',
        BADGE_STYLE_CLASS[label] || 'border-border bg-white/80 text-muted dark:bg-slate-900/60'
      )}
    >
      {label}
    </span>
  );
};

const ModelOptionsList = ({ value, onChange, onSelect, className, style, menuRef, models }) => (
  <ul ref={menuRef} role="listbox" className={className} style={style}>
    {models.map((model) => {
      const isActive = model.key === value;
      const badge = getLlmModelBadgeByKey(model.key, models);

      return (
        <li key={model.key} role="option" aria-selected={isActive}>
          <button
            type="button"
            onClick={() => {
              onChange(model.key);
              onSelect?.();
            }}
            className={classNames(
              'flex w-full items-center gap-2 px-3 py-2.5 text-start text-xs font-semibold transition',
              isActive
                ? 'bg-accent-soft text-accent'
                : 'text-text hover:bg-white/80 dark:hover:bg-slate-900/70'
            )}
          >
            <ProviderDot provider={model.provider} size="md" />
            <span className="min-w-0 flex-1 truncate">{model.name}</span>
            <ModelBadge label={badge} />
          </button>
        </li>
      );
    })}
  </ul>
);

export const ModelSelect = ({
  value,
  onChange,
  disabled = false,
  compact = false,
  field = false,
  portal = false,
  showBadge = false
}) => {
  const { models: catalogModels } = useLlmCatalog();
  const models = useMemo(() => {
    const chatModels = catalogModels.filter((model) => (model.category || 'chat') === 'chat');
    const source = chatModels.length ? chatModels : LLM_MODELS;
    return source.map((model) => ({
      key: model.key,
      name: model.name,
      provider: model.provider,
      badge: model.badge
    }));
  }, [catalogModels]);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const selectedModel = getLlmModelByKey(value, models);
  const selectedModelBadge = getLlmModelBadgeByKey(selectedModel?.key, models);
  const usesFixedMenu = field || (compact && portal);

  const updateMenuPosition = useCallback(() => {
    if (!usesFixedMenu || !triggerRef.current) {
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP;
    const spaceAbove = rect.top - MENU_GAP;
    const openUpward = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    const availableHeight = Math.min(
      MENU_MAX_HEIGHT,
      Math.max(120, (openUpward ? spaceAbove : spaceBelow) - 4)
    );

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 208),
      zIndex: 70,
      maxHeight: availableHeight,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + MENU_GAP }
        : { top: rect.bottom + MENU_GAP })
    });
  }, [usesFixedMenu]);

  useEffect(() => {
    if (!open || !usesFixedMenu) {
      setMenuStyle(null);
      return undefined;
    }

    updateMenuPosition();
    triggerRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    const handleReposition = () => updateMenuPosition();

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updateMenuPosition, usesFixedMenu]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  if (!selectedModel) {
    return null;
  }

  const menuClassName = classNames(
    'overflow-y-auto rounded-2xl border border-border bg-panel-strong py-1 shadow-floating backdrop-blur-xl',
    !usesFixedMenu && 'absolute z-30 max-h-64 bottom-full end-0 mb-2 min-w-[13rem]'
  );

  const closeMenu = () => setOpen(false);

  const menu =
    open && (!usesFixedMenu || menuStyle) ? (
      <ModelOptionsList
        value={value}
        onChange={onChange}
        onSelect={closeMenu}
        models={models}
        menuRef={menuRef}
        className={menuClassName}
        style={usesFixedMenu ? menuStyle : undefined}
      />
    ) : null;

  return (
    <div ref={rootRef} className={classNames('relative', field && 'w-full')}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={classNames(
          'inline-flex items-center border border-border text-text transition hover:border-accent/30 hover:bg-white/80 dark:hover:bg-slate-900/50',
          field
            ? 'w-full gap-2 rounded-2xl bg-white/80 px-4 py-3 text-sm font-medium dark:bg-slate-950/40'
            : 'gap-1 rounded-lg',
          !field && compact && !showBadge && 'max-w-[7rem] px-1.5 py-1 text-[10px] font-medium',
          !field && compact && showBadge && 'max-w-[11rem] gap-1 px-2 py-1 text-[10px] font-medium',
          !field &&
            !compact &&
            'max-w-[10.5rem] gap-1.5 px-2.5 py-2 text-[11px] font-semibold bg-white/70 dark:bg-slate-950/35',
          !field && compact && 'border-transparent bg-transparent',
          disabled && 'cursor-not-allowed opacity-50',
          open &&
            (field
              ? 'border-accent bg-white dark:bg-slate-950/60'
              : compact
                ? 'text-accent'
                : 'border-accent/35 bg-accent-soft/60')
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={compact && !field ? selectedModel.name : undefined}
      >
        {!compact || field ? (
          <Cpu size={field ? 16 : 14} className="shrink-0 text-accent/90" aria-hidden="true" />
        ) : null}
        <ProviderDot provider={selectedModel.provider} />
        <span className={classNames('truncate', field && 'min-w-0 flex-1 text-start')}>
          {selectedModel.name}
        </span>
        {!compact || field || showBadge ? <ModelBadge label={selectedModelBadge} /> : null}
        <ChevronDown
          size={field ? 16 : compact ? 12 : 14}
          className={classNames('shrink-0 opacity-60 transition', open && 'rotate-180', field && 'ms-auto')}
        />
      </button>

      {menu
        ? usesFixedMenu && typeof document !== 'undefined'
          ? createPortal(menu, document.body)
          : menu
        : null}
    </div>
  );
};
